<template>
  <div class="login-container">
    <aside class="login-visual" aria-labelledby="login-visual-title">
      <img
        class="login-visual-image"
        :src="loginVisual"
        alt="头盔与双仓消毒设备示意图"
        width="900"
        height="1350"
        fetchpriority="high"
        decoding="async"
      />
      <div class="login-visual-content">
        <span class="login-brand">HelmetCare</span>
        <div class="login-visual-copy">
          <p>设备监控 · 服务管理 · 运营分析</p>
          <h2 id="login-visual-title">头盔消毒设备<br />物联网运营平台</h2>
        </div>
      </div>
    </aside>
    <div class="login-card">
      <h1>HelmetCare 管理后台</h1>

      <!-- Login Form -->
      <form v-if="!mfaPending" @submit.prevent="handleLogin">
        <div v-if="authStore.error.value" class="error-message">
          {{ authStore.error.value }}
        </div>

        <div class="form-group">
          <label for="email">邮箱</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            placeholder="输入邮箱"
            required
            :disabled="authStore.isLoading.value"
          />
        </div>

        <div class="form-group">
          <label for="password">密码</label>
          <input
            id="password"
            v-model="form.password"
            type="password"
            placeholder="输入密码"
            required
            :disabled="authStore.isLoading.value"
          />
        </div>

        <button type="submit" class="login-button" :disabled="authStore.isLoading.value">
          {{ authStore.isLoading.value ? '登录中...' : '登录' }}
        </button>
      </form>

      <!-- MFA Form -->
      <form v-else @submit.prevent="handleMfaVerify">
        <div v-if="authStore.error.value" class="error-message">
          {{ authStore.error.value }}
        </div>

        <p class="mfa-description">请输入您的 6 位身份验证码</p>

        <div class="form-group">
          <label for="totp-code">验证码</label>
          <input
            id="totp-code"
            v-model="mfaForm.totpCode"
            type="text"
            placeholder="000000"
            :maxlength="6"
            required
            :disabled="authStore.isLoading.value"
          />
        </div>

        <button type="submit" class="login-button" :disabled="authStore.isLoading.value">
          {{ authStore.isLoading.value ? '验证中...' : '验证' }}
        </button>

        <button type="button" class="back-button" @click="goBackToLogin">返回</button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import authStore from '../stores/auth';
import loginVisual from '../assets/login-helmet-care.webp';

const router = useRouter();

const form = reactive({
  email: '',
  password: '',
});

interface MfaFormData {
  totpCode: string;
}

const mfaForm = reactive<MfaFormData>({
  totpCode: '',
});

const mfaPending = ref(false);
let currentSessionId = '';

async function handleLogin() {
  try {
    const result = await authStore.login(form.email, form.password);

    if (result.mfaRequired) {
      currentSessionId = result.sessionId;
      mfaPending.value = true;
      mfaForm.totpCode = '';
      authStore.error.value = null;
    } else {
      // Direct login success
      await router.push('/overview');
    }
  } catch (err) {
    // Error is already handled by authStore
  }
}

async function handleMfaVerify() {
  try {
    await authStore.verifyMfa(currentSessionId, mfaForm.totpCode);
    await router.push('/overview');
  } catch (err) {
    // Error is already handled by authStore
  }
}

function goBackToLogin() {
  mfaPending.value = false;
  mfaForm.totpCode = '';
  authStore.error.value = null;
}
</script>

<style scoped>
.login-container {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  min-height: 100vh;
  background: var(--hc-surface-0);
  font-family: var(--hc-font-sans);
}

.login-visual {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  background: var(--hc-nav-bg);
  color: #f0f5f3;
}

.login-visual-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 56%;
  filter: hue-rotate(-45deg) saturate(0.65) brightness(0.88);
}

.login-visual::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgb(10 16 14 / 68%) 0%,
    rgb(10 16 14 / 8%) 30%,
    rgb(10 16 14 / 4%) 58%,
    rgb(10 16 14 / 94%) 100%
  );
}

.login-visual-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 48px clamp(32px, 4vw, 64px);
}

.login-brand {
  font-family: var(--hc-font-display);
  font-size: 23px;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.login-visual-copy p {
  margin: 0 0 16px;
  color: #b7ccc4;
  font-size: 12px;
  letter-spacing: 0.08em;
}

.login-visual-copy h2 {
  margin: 0;
  font-size: clamp(28px, 2.6vw, 42px);
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.02em;
}

.login-card {
  align-self: center;
  justify-self: start;
  width: 100%;
  max-width: 372px;
  padding: 0 0 0 64px;
  animation: hc-rise 520ms var(--hc-ease) backwards;
}

.login-card h1 {
  margin: 0 0 6px;
  font-family: var(--hc-font-display);
  font-size: 27px;
  font-weight: 500;
  letter-spacing: -0.015em;
  color: var(--hc-text-1);
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  color: var(--hc-text-2);
  font-size: 12.5px;
}

.form-group input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--hc-line);
  border-radius: var(--hc-radius-sm);
  font-size: 14px;
  font-family: inherit;
  background: var(--hc-surface-1);
  color: var(--hc-text-1);
  transition: border-color var(--hc-dur-micro) var(--hc-ease);
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: var(--hc-brand-500);
}

.login-button {
  width: 100%;
  padding: 10px;
  margin-top: 20px;
  background: var(--hc-brand-500);
  color: #fff;
  border: none;
  border-radius: var(--hc-radius-sm);
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: background-color var(--hc-dur-micro) var(--hc-ease);
}

.login-button:hover:not(:disabled) {
  background: var(--hc-brand-600);
}

.login-button:disabled {
  background: var(--hc-idle);
  cursor: not-allowed;
}

.back-button {
  width: 100%;
  padding: 9px;
  margin-top: 10px;
  background: transparent;
  color: var(--hc-text-2);
  border: 1px solid var(--hc-line);
  border-radius: var(--hc-radius-sm);
  font-size: 13.5px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color var(--hc-dur-micro) var(--hc-ease);
}

.back-button:hover:not(:disabled) {
  border-color: var(--hc-line-strong);
}

.error-message {
  background: var(--hc-danger-bg);
  border-radius: var(--hc-radius-sm);
  padding: 10px 12px;
  margin-bottom: 16px;
  color: var(--hc-danger);
  font-size: 13px;
}

.mfa-description {
  color: var(--hc-text-2);
  margin-bottom: 18px;
  font-size: 13.5px;
}

@media (max-width: 860px) {
  .login-container {
    grid-template-columns: 1fr;
  }

  .login-visual {
    display: none;
  }

  .login-card {
    justify-self: center;
    padding: 0 24px;
  }
}
</style>
